// Versão: 1.02
// Data..: 2026-10-03
// v1.02: FEAT-002 — aba "Bater ponto" reescrita com a máquina de estados dos 4 eventos
//        (Entrada/Iniciar almoço/Voltar do almoço/Saída), timeline do dia com rótulos
//        pt-BR + IP + coluna de hora extra, Modal exigindo motivo ao marcar saída com
//        hora extra, botão "Cancelar hora extra" (só ADMIN) via Modal, e botão "Meu
//        relatório" (PDF/CSV do próprio funcionário por período). Pop-ups via Modal.tsx.
// v1.01: compatibilidade com o modelo de 4 eventos do ponto — botões de entrada/saída
//        enviam ENTRY/EXIT e a lista do dia usa EXIT para o ícone de saída (a máquina
//        de estados completa com almoço/hora extra vem na FEAT-002).
// Ponto Eletrônico (frontend). Abas condicionais ao papel:
//  - "Bater ponto" (ADMIN/AGENT): marca entrada/almoço/saída, lista o dia e o resumo.
//  - "Justificativas" (ADMIN/AGENT): envia ausências com anexos (imagens comprimidas).
//  - "Relatórios" (ADMIN/ACCOUNTING): visualiza/baixa HTML, PDF e CSV do período.
// Downloads binários seguem o padrão getToken()+fetch+blob+createObjectURL (molde
// InvoicesPage.viewBoleto). Pop-ups usam o componente Modal.

import { useEffect, useMemo, useState, type FormEvent } from 'react';
import {
  UserRole,
  PunchKindLabels,
  type DaySummary,
  type Punch,
  type PunchKind,
  type TimeclockConfig,
  type AbsenceJustification,
  type AbsenceCreateResult,
  type User,
  type Paginated,
} from '@makweb/shared';
import { Link } from 'react-router-dom';
import { api } from '../lib/api.js';
import { config } from '../config.js';
import { getToken } from '../lib/auth.js';
import { useAuth } from '../context/AuthContext.js';
import { useToast } from '../context/ToastContext.js';
import { PageHeader } from '../components/PageHeader.js';
import { Modal } from '../components/Modal.js';
import { compressImage } from '../lib/imageCompress.js';

type Tab = 'punch' | 'absence' | 'report';

// Data de hoje em AAAA-MM-DD (fuso local do navegador — suficiente para os filtros).
const todayIso = () => new Date().toISOString().slice(0, 10);
// Primeiro dia do mês civil atual em AAAA-MM-DD.
const monthStartIso = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
};
// Formata minutos como "Xh YYm".
const fmtMinutes = (min: number) => {
  const h = Math.floor(min / 60);
  const m = min % 60;
  return `${h}h ${String(m).padStart(2, '0')}m`;
};
// Formata um instante ISO como HH:mm no fuso America/Sao_Paulo.
const fmtTime = (iso: string) =>
  new Date(iso).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', timeZone: 'America/Sao_Paulo' });
// Formata AAAA-MM-DD como dd/mm/aaaa (sem fuso).
const dateBr = (iso: string) => {
  const d = new Date(`${iso.slice(0, 10)}T00:00:00Z`);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString('pt-BR', { timeZone: 'UTC' });
};

// Minutos locais (0..1439) de um instante ISO no fuso America/Sao_Paulo. Usado para
// detectar hora extra no front (saída depois do endAt configurado).
const localMinutesOf = (iso: string) => {
  const parts = new Intl.DateTimeFormat('pt-BR', {
    hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'America/Sao_Paulo',
  }).formatToParts(new Date(iso));
  const h = Number(parts.find((p) => p.type === 'hour')?.value ?? '0');
  const m = Number(parts.find((p) => p.type === 'minute')?.value ?? '0');
  return h * 60 + m;
};

// Converte 'HH:mm' em minutos do dia; retorna null quando o formato é inválido/ausente.
const hhmmToMinutes = (hhmm?: string): number | null => {
  if (!hhmm || !/^\d{2}:\d{2}$/.test(hhmm)) return null;
  const [h, m] = hhmm.split(':').map(Number);
  if (h > 23 || m > 59) return null;
  return h * 60 + m;
};

// Estado derivado do dia (espelha deriveDayState do backend/timeclockService.ts).
// Decide quais botões aparecem na aba "Bater ponto".
interface DayState {
  openEntry: boolean;
  lunchOpen: boolean;
  lunchDone: boolean;
  canEntry: boolean;
  canExit: boolean;
  canLunchStart: boolean;
  canLunchEnd: boolean;
}
const deriveDayState = (punches: Punch[]): DayState => {
  const ordered = [...punches].sort((a, b) => (a.at < b.at ? -1 : a.at > b.at ? 1 : 0));
  let openEntry = false;
  let lunchOpen = false;
  let lunchDone = false;
  for (const p of ordered) {
    // Leitura defensiva do legado 'in'/'out' (mesmo mapeamento do normalizeKind).
    const raw = p.kind as string;
    const kind = raw === 'in' ? 'ENTRY' : raw === 'out' ? 'EXIT' : raw;
    switch (kind) {
      case 'ENTRY': openEntry = true; lunchOpen = false; lunchDone = false; break;
      case 'LUNCH_START': lunchOpen = true; break;
      case 'LUNCH_END': lunchOpen = false; lunchDone = true; break;
      case 'EXIT': openEntry = false; lunchOpen = false; lunchDone = false; break;
    }
  }
  return {
    openEntry, lunchOpen, lunchDone,
    canEntry: !openEntry,
    canExit: openEntry && !lunchOpen,
    canLunchStart: openEntry && !lunchOpen && !lunchDone,
    canLunchEnd: lunchOpen,
  };
};

export function TimeclockPage() {
  const { user } = useAuth();
  const role = user?.role;
  const canPunch = role === UserRole.ADMIN || role === UserRole.AGENT;
  const canReport = role === UserRole.ADMIN || role === UserRole.ACCOUNTING;

  // Aba inicial depende do papel (contabilidade só vê relatórios).
  const [tab, setTab] = useState<Tab>(canPunch ? 'punch' : 'report');

  return (
    <div style={{ maxWidth: 980, margin: '0 auto' }}>
      <PageHeader eyebrow="Gestão" title="Ponto" description="Registro de ponto, justificativas de ausência e relatórios." />

      <div className="flex gap-2 mb-4 flex-wrap">
        {canPunch && (
          <button className={`btn btn-sm ${tab === 'punch' ? 'btn-primary' : 'btn-ghost'}`} onClick={() => setTab('punch')}>🕒 Bater ponto</button>
        )}
        {canPunch && (
          <button className={`btn btn-sm ${tab === 'absence' ? 'btn-primary' : 'btn-ghost'}`} onClick={() => setTab('absence')}>📎 Justificativas</button>
        )}
        {canReport && (
          <button className={`btn btn-sm ${tab === 'report' ? 'btn-primary' : 'btn-ghost'}`} onClick={() => setTab('report')}>📊 Relatórios</button>
        )}
      </div>

      {tab === 'punch' && canPunch && <PunchTab />}
      {tab === 'absence' && canPunch && <AbsenceTab />}
      {tab === 'report' && canReport && <ReportTab />}
    </div>
  );
}

// ===== Aba: Bater ponto =====
function PunchTab() {
  const toast = useToast();
  const { user } = useAuth();
  const isAdmin = user?.role === UserRole.ADMIN;
  const [days, setDays] = useState<DaySummary[]>([]);
  const [config2, setConfig2] = useState<TimeclockConfig | null>(null);
  const [configLoaded, setConfigLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);

  // Modal de hora extra (motivo/nº do chamado obrigatório ao marcar saída após o endAt).
  const [overtimeOpen, setOvertimeOpen] = useState(false);
  const [overtimeReason, setOvertimeReason] = useState('');

  // Modal de cancelamento da hora extra (somente ADMIN).
  const [cancelTarget, setCancelTarget] = useState<Punch | null>(null);
  const [cancelReason, setCancelReason] = useState('');

  // Modal "Meu relatório" (período do próprio funcionário).
  const [reportOpen, setReportOpen] = useState(false);
  const [reportFrom, setReportFrom] = useState(monthStartIso());
  const [reportTo, setReportTo] = useState(todayIso());

  const load = () => {
    setLoading(true);
    const from = monthStartIso();
    const to = todayIso();
    api.get<{ days: DaySummary[] }>(`/me/timeclock?from=${from}&to=${to}`)
      .then((r) => setDays(r.days))
      .catch((e) => toast.error(e instanceof Error ? e.message : 'Falha ao carregar as marcações'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    api.get<{ config: TimeclockConfig | null }>('/me/timeclock/config')
      .then((r) => setConfig2(r.config))
      .catch(() => setConfig2(null))
      .finally(() => setConfigLoaded(true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Dia local de hoje (America/Sao_Paulo) e seu resumo (se houver).
  const todayKey = new Date().toLocaleDateString('en-CA', { timeZone: 'America/Sao_Paulo' });
  const today = useMemo(() => days.find((d) => d.date === todayKey), [days, todayKey]);

  // Estado derivado do dia de hoje — habilita/esconde os botões.
  const state = useMemo(() => deriveDayState(today?.punches ?? []), [today]);

  // Verifica se o horário-alvo ainda não foi configurado (banner de aviso).
  const notConfigured = configLoaded && (!config2 || (!config2.startAt && !config2.lunchOutAt && !config2.lunchInAt && !config2.endAt));

  // Envia a marcação; EXIT pode levar o motivo de hora extra.
  const sendPunch = async (kind: PunchKind, reason?: string) => {
    setBusy(true);
    try {
      await api.post('/me/timeclock/punch', { kind, overtimeReason: reason });
      const msg: Record<string, string> = {
        ENTRY: 'Entrada registrada',
        LUNCH_START: 'Início do almoço registrado',
        LUNCH_END: 'Volta do almoço registrada',
        EXIT: 'Saída registrada',
      };
      toast.success(msg[kind] ?? 'Ponto registrado');
      load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Falha ao registrar o ponto');
    } finally {
      setBusy(false);
    }
  };

  // Clique em "Marcar saída": se o horário local já passou do endAt configurado, abre o
  // Modal exigindo o motivo (hora extra); caso contrário marca direto.
  const onExitClick = () => {
    const endMin = hhmmToMinutes(config2?.endAt);
    const nowMin = localMinutesOf(new Date().toISOString());
    if (endMin !== null && nowMin > endMin) {
      setOvertimeReason('');
      setOvertimeOpen(true);
      return;
    }
    sendPunch('EXIT');
  };

  // Confirma a saída com hora extra (motivo obrigatório).
  const confirmOvertime = async (e: FormEvent) => {
    e.preventDefault();
    const reason = overtimeReason.trim();
    if (!reason) { toast.error('Informe o motivo / nº do chamado da hora extra.'); return; }
    setOvertimeOpen(false);
    await sendPunch('EXIT', reason);
  };

  // Confirma o cancelamento da hora extra de uma marcação (somente ADMIN).
  const confirmCancel = async (e: FormEvent) => {
    e.preventDefault();
    if (!cancelTarget) return;
    const reason = cancelReason.trim();
    if (!reason) { toast.error('Informe o motivo do cancelamento.'); return; }
    setBusy(true);
    try {
      await api.post('/timeclock/overtime/cancel', {
        userId: cancelTarget.userId,
        punchId: cancelTarget.id,
        reason,
      });
      toast.success('Hora extra cancelada');
      setCancelTarget(null);
      setCancelReason('');
      load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Falha ao cancelar a hora extra');
    } finally {
      setBusy(false);
    }
  };

  // Baixa/abre o "Meu relatório" no formato indicado (sem userId; o backend escopa ao próprio AGENT).
  const fetchMyReport = async (format: 'pdf' | 'csv') => {
    if (!reportFrom || !reportTo) { toast.error('Informe o período (de/até).'); return; }
    setBusy(true);
    try {
      const token = await getToken();
      const params = new URLSearchParams({ from: reportFrom, to: reportTo, format });
      const res = await fetch(`${config.apiUrl}/timeclock/report?${params.toString()}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error('Não foi possível gerar o relatório.');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      if (format === 'csv') {
        const a = document.createElement('a');
        a.href = url;
        a.download = `meu_ponto_${reportFrom}_a_${reportTo}.csv`;
        document.body.appendChild(a);
        a.click();
        a.remove();
      } else {
        window.open(url, '_blank', 'noopener,noreferrer');
      }
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Falha ao gerar o relatório');
    } finally {
      setBusy(false);
    }
  };

  // Minutos de hora extra "ao vivo" para a marcação EXIT (quando o backend não gravou o campo).
  const liveOvertime = (p: Punch) => {
    if (typeof p.overtimeMinutes === 'number') return p.overtimeMinutes;
    const endMin = hhmmToMinutes(config2?.endAt);
    if (endMin === null) return 0;
    return Math.max(0, localMinutesOf(p.at) - endMin);
  };

  return (
    <div>
      {notConfigured && (
        <div className="card mb-3" style={{ borderLeft: '3px solid var(--warning)' }}>
          <strong>⚠ Defina seu horário de trabalho.</strong>
          <div className="text-dim text-sm mt-1">
            Configure os horários de entrada, almoço e saída para receber os alertas da jornada.{' '}
            <Link to="/account" className="text-sm">Ir para Minha Conta ↗</Link>
          </div>
        </div>
      )}

      <div className="card mb-3">
        <div className="flex gap-2 flex-wrap">
          {state.canEntry && (
            <button className="btn btn-primary" onClick={() => sendPunch('ENTRY')} disabled={busy}>⬇ Marcar entrada</button>
          )}
          {state.canLunchStart && (
            <button className="btn btn-ghost" onClick={() => sendPunch('LUNCH_START')} disabled={busy}>🍽 Iniciar almoço</button>
          )}
          {state.canLunchEnd && (
            <button className="btn btn-ghost" onClick={() => sendPunch('LUNCH_END')} disabled={busy}>↩ Voltar do almoço</button>
          )}
          {state.canExit && (
            <button className="btn btn-ghost" onClick={onExitClick} disabled={busy}>⬆ Marcar saída</button>
          )}
          <button className="btn btn-ghost" onClick={() => setReportOpen(true)} disabled={busy} style={{ marginLeft: 'auto' }}>📄 Meu relatório</button>
        </div>
        {today && (
          <div className="text-dim text-sm mt-3">
            Hoje: trabalhado {fmtMinutes(today.workedMinutes)} · intervalo {fmtMinutes(today.breakMinutes)}
            {today.overtimeMinutes > 0 && !today.overtimeCanceled && (
              <span style={{ color: 'var(--primary)', marginLeft: 6 }}>⏱ hora extra {fmtMinutes(today.overtimeMinutes)}</span>
            )}
            {today.hasAnomaly && <span style={{ color: 'var(--warning)', marginLeft: 6 }}>⚠ jornada em aberto</span>}
          </div>
        )}
      </div>

      <div className="card card-flush">
        {days.length === 0 ? (
          <div className="empty"><div className="empty-icon">🕒</div>{loading ? 'Carregando…' : 'Nenhuma marcação neste mês.'}</div>
        ) : (
          <table className="table"><thead><tr>
            <th>Dia</th><th>Marcações</th><th>Trabalhado</th><th>Intervalo</th><th>Hora extra</th><th></th>
          </tr></thead><tbody>
            {days.map((d) => {
              // Marcação EXIT com hora extra do dia (alvo do cancelamento pelo admin).
              const overtimePunch = d.punches.find((p) => p.isOvertime && !p.overtimeCanceled && liveOvertime(p) > 0);
              return (
                <tr key={d.date}>
                  <td className="font-semibold">{dateBr(d.date)}</td>
                  <td className="text-sm text-muted">
                    {d.punches.length === 0
                      ? '—'
                      : d.punches.map((p) => (
                        <div key={p.id} title={`IP ${p.ip}`}>
                          {PunchKindLabels[p.kind] ?? p.kind} · {fmtTime(p.at)} · <span className="text-xs">IP {p.ip}</span>
                        </div>
                      ))}
                  </td>
                  <td className="text-sm">{fmtMinutes(d.workedMinutes)}</td>
                  <td className="text-sm">{fmtMinutes(d.breakMinutes)}</td>
                  <td className="text-sm">
                    {d.overtimeCanceled ? (
                      <span className="text-dim" title="Hora extra cancelada pelo administrador">cancelada</span>
                    ) : d.overtimeMinutes > 0 ? (
                      fmtMinutes(d.overtimeMinutes)
                    ) : (
                      '—'
                    )}
                  </td>
                  <td className="text-right">
                    {d.hasAnomaly && <span title="Jornada em aberto / inconsistente" style={{ color: 'var(--warning)', marginRight: 6 }}>⚠</span>}
                    {isAdmin && overtimePunch && !d.overtimeCanceled && (
                      <button
                        className="btn btn-sm btn-ghost"
                        onClick={() => { setCancelTarget(overtimePunch); setCancelReason(''); }}
                        disabled={busy}
                        title="Cancelar a hora extra deste dia"
                      >
                        Cancelar extra
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody></table>
        )}
      </div>

      {/* Modal: motivo da hora extra (obrigatório) ao marcar saída após o endAt. */}
      <Modal
        open={overtimeOpen}
        title="Hora extra — informe o motivo"
        onClose={() => setOvertimeOpen(false)}
        footer={
          <>
            <button className="btn btn-ghost" type="button" onClick={() => setOvertimeOpen(false)}>Cancelar</button>
            <button className="btn btn-primary" type="submit" form="overtime-form" disabled={busy}>Confirmar saída</button>
          </>
        }
      >
        <form id="overtime-form" onSubmit={confirmOvertime}>
          <p className="text-muted text-sm mb-2">
            Sua saída está após o horário configurado, caracterizando hora extra. Informe o motivo ou o nº do chamado.
          </p>
          <div className="field mb-0">
            <label className="label">Motivo / nº do chamado</label>
            <textarea className="input" rows={3} value={overtimeReason} onChange={(e) => setOvertimeReason(e.target.value)} required autoFocus />
          </div>
        </form>
      </Modal>

      {/* Modal: cancelamento da hora extra (somente ADMIN). */}
      <Modal
        open={!!cancelTarget}
        title="Cancelar hora extra"
        onClose={() => setCancelTarget(null)}
        footer={
          <>
            <button className="btn btn-ghost" type="button" onClick={() => setCancelTarget(null)}>Fechar</button>
            <button className="btn btn-primary" type="submit" form="cancel-overtime-form" disabled={busy}>Confirmar cancelamento</button>
          </>
        }
      >
        <form id="cancel-overtime-form" onSubmit={confirmCancel}>
          <p className="text-muted text-sm mb-2">
            A hora extra será descontada do total, mantendo o rastro do cancelamento. Informe o motivo.
          </p>
          <div className="field mb-0">
            <label className="label">Motivo do cancelamento</label>
            <textarea className="input" rows={3} value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} required autoFocus />
          </div>
        </form>
      </Modal>

      {/* Modal: Meu relatório (período + PDF/CSV do próprio funcionário). */}
      <Modal
        open={reportOpen}
        title="Meu relatório de ponto"
        onClose={() => setReportOpen(false)}
        footer={
          <>
            <button className="btn btn-ghost" type="button" onClick={() => setReportOpen(false)}>Fechar</button>
            <button className="btn btn-ghost" type="button" onClick={() => fetchMyReport('csv')} disabled={busy}>📑 CSV</button>
            <button className="btn btn-primary" type="button" onClick={() => fetchMyReport('pdf')} disabled={busy}>📄 PDF</button>
          </>
        }
      >
        <div className="flex gap-2 items-end flex-wrap">
          <div className="field mb-0"><label className="label">De</label>
            <input className="input" type="date" value={reportFrom} onChange={(e) => setReportFrom(e.target.value)} />
          </div>
          <div className="field mb-0"><label className="label">Até</label>
            <input className="input" type="date" value={reportTo} onChange={(e) => setReportTo(e.target.value)} />
          </div>
        </div>
      </Modal>
    </div>
  );
}

// ===== Aba: Justificativas =====
function AbsenceTab() {
  const toast = useToast();
  const [list, setList] = useState<AbsenceJustification[]>([]);
  const [loading, setLoading] = useState(false);
  const [date, setDate] = useState(todayIso());
  const [reason, setReason] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    const from = monthStartIso();
    const to = todayIso();
    api.get<{ absences: AbsenceJustification[] }>(`/me/timeclock/absences?from=${from}&to=${to}`)
      .then((r) => setList(r.absences))
      .catch((e) => toast.error(e instanceof Error ? e.message : 'Falha ao carregar as justificativas'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!date) { toast.error('Informe a data da ausência.'); return; }
    setSaving(true);
    try {
      // Comprime as imagens (PDF só é validado) antes de pedir as URLs assinadas.
      const prepared = [];
      for (const f of files) {
        prepared.push(await compressImage(f));
      }

      const result = await api.post<AbsenceCreateResult>('/me/timeclock/absence', {
        date,
        reason: reason.trim() || undefined,
        files: prepared.map((p) => ({ fileName: p.fileName, contentType: p.contentType, size: p.size })),
      });

      // Faz o PUT direto em cada URL assinada com o mesmo Content-Type assinado.
      for (let i = 0; i < result.uploads.length; i++) {
        const up = result.uploads[i];
        const p = prepared[i];
        const res = await fetch(up.uploadUrl, { method: 'PUT', headers: { 'Content-Type': p.contentType }, body: p.blob });
        if (!res.ok) throw new Error(`Falha ao enviar o arquivo "${p.fileName}".`);
      }

      if (prepared.some((p) => p.warned)) {
        toast.success('Justificativa registrada (alguns arquivos não puderam ser comprimidos).');
      } else {
        toast.success('Justificativa registrada');
      }
      setDate(todayIso()); setReason(''); setFiles([]);
      load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Falha ao registrar a justificativa');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <form onSubmit={submit} className="card mb-3">
        <h3>Registrar justificativa de ausência</h3>
        <div className="field-pair">
          <div className="field mb-0"><label className="label">Data da ausência</label>
            <input className="input" type="date" value={date} onChange={(e) => setDate(e.target.value)} required />
          </div>
          <div className="field mb-0"><label className="label">Anexos (opcional)</label>
            <input
              className="input"
              type="file"
              multiple
              accept="image/jpeg,image/png,application/pdf"
              onChange={(e) => setFiles(Array.from(e.target.files ?? []))}
            />
          </div>
        </div>
        <div className="field mt-2"><label className="label">Motivo (opcional)</label>
          <textarea className="input" rows={3} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Ex.: Atestado médico" />
        </div>
        {files.length > 0 && (
          <div className="text-dim text-xs mb-2">{files.length} arquivo(s) selecionado(s). Imagens são comprimidas automaticamente.</div>
        )}
        <button className="btn btn-primary" type="submit" disabled={saving}>{saving ? 'Enviando…' : 'Registrar justificativa'}</button>
      </form>

      <div className="card card-flush">
        {list.length === 0 ? (
          <div className="empty"><div className="empty-icon">📎</div>{loading ? 'Carregando…' : 'Nenhuma justificativa neste mês.'}</div>
        ) : (
          <table className="table"><thead><tr>
            <th>Data</th><th>Motivo</th><th>Anexos</th>
          </tr></thead><tbody>
            {list.map((a) => (
              <tr key={a.id}>
                <td className="font-semibold">{dateBr(a.date)}</td>
                <td className="text-sm text-muted">{a.reason || '—'}</td>
                <td className="text-sm">{a.attachments.length}</td>
              </tr>
            ))}
          </tbody></table>
        )}
      </div>
    </div>
  );
}

// ===== Aba: Relatórios =====
function ReportTab() {
  const toast = useToast();
  const { user } = useAuth();
  const isAdmin = user?.role === UserRole.ADMIN;
  const [from, setFrom] = useState(monthStartIso());
  const [to, setTo] = useState(todayIso());
  const [userId, setUserId] = useState('');
  const [users, setUsers] = useState<User[]>([]);
  const [busy, setBusy] = useState(false);

  // ADMIN/ACCOUNTING podem filtrar por funcionário: carrega a lista de usuários staff.
  useEffect(() => {
    api.get<Paginated<User>>('/users')
      .then((r) => setUsers(r.items.filter((u) => u.role === UserRole.ADMIN || u.role === UserRole.AGENT)))
      .catch(() => {});
  }, []);

  // Monta a query string do relatório para o formato pedido.
  const buildQuery = (format: 'html' | 'pdf' | 'csv') => {
    const params = new URLSearchParams();
    params.set('from', from);
    params.set('to', to);
    params.set('format', format);
    if (userId) params.set('userId', userId);
    return params.toString();
  };

  // Baixa/abre o relatório no formato indicado (download binário autenticado).
  const fetchReport = async (format: 'html' | 'pdf' | 'csv') => {
    if (!from || !to) { toast.error('Informe o período (de/até).'); return; }
    setBusy(true);
    try {
      const token = await getToken();
      const res = await fetch(`${config.apiUrl}/timeclock/report?${buildQuery(format)}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error('Não foi possível gerar o relatório.');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      if (format === 'csv') {
        // CSV: força o download com um nome amigável.
        const a = document.createElement('a');
        a.href = url;
        a.download = `ponto_${from}_a_${to}.csv`;
        document.body.appendChild(a);
        a.click();
        a.remove();
      } else {
        // HTML/PDF: abre em nova aba.
        window.open(url, '_blank', 'noopener,noreferrer');
      }
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Falha ao gerar o relatório');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card">
      <h3>Relatório de ponto</h3>
      <p className="text-muted text-sm">
        {isAdmin ? 'Gere o relatório de todos os funcionários ou filtre por um deles.' : 'Gere o relatório de ponto dos funcionários no período.'}
      </p>
      <div className="flex gap-2 items-end flex-wrap mt-2">
        <div className="field mb-0"><label className="label">De</label>
          <input className="input" type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
        </div>
        <div className="field mb-0"><label className="label">Até</label>
          <input className="input" type="date" value={to} onChange={(e) => setTo(e.target.value)} />
        </div>
        <div className="field mb-0" style={{ minWidth: 200 }}>
          <label className="label">Funcionário</label>
          <select className="select" value={userId} onChange={(e) => setUserId(e.target.value)}>
            <option value="">Todos</option>
            {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
        </div>
      </div>
      <div className="flex gap-2 mt-3 flex-wrap">
        <button className="btn btn-ghost" onClick={() => fetchReport('html')} disabled={busy}>👁 Visualizar</button>
        <button className="btn btn-primary" onClick={() => fetchReport('pdf')} disabled={busy}>📄 PDF</button>
        <button className="btn btn-ghost" onClick={() => fetchReport('csv')} disabled={busy}>📑 CSV</button>
      </div>
    </div>
  );
}
